import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import Sidebar from "../../components/Dashboard/Sidebar";
import axios from "axios";
import toast from "react-hot-toast";
import { FiEdit2, FiTrash2, FiPlus, FiX, FiImage } from "react-icons/fi";
import { useAuth } from "../../context/auth";
import Swal from "sweetalert2";

const BASE_IMAGE_URL = process.env.REACT_APP_API;

function getCategoryImage(cat) {
  if (cat.image && cat.image.startsWith("http")) return cat.image;
  if (cat.image) return `${BASE_IMAGE_URL}/${cat.image}`;
  return null;
}

const CategoryManagement = () => {
  const [auth] = useAuth();
  const [categories, setCategories] = useState([]);
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [name, setName] = useState("");
  const [image, setImage] = useState(null);

  const [editCategory, setEditCategory] = useState(null);
  const [editName, setEditName] = useState("");
  const [editImage, setEditImage] = useState(null);

  const [loading, setLoading] = useState(false);

  const getAllCategories = async () => {
    if (loading) return;
    try {
      setLoading(true);
      const { data } = await axios.get("/api/v1/category/get-category");
      if (data?.success) setCategories(data.category);
    } catch {
      toast.error("Failed to fetch categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllCategories();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!name.trim()) return toast.error("Category name is required");
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("name", name);
      if (image) formData.append("image", image);
      const { data } = await axios.post(
        "/api/v1/category/create-category",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (data.success) {
        toast.success("Category created");
        setName("");
        setImage(null);
        setCreateModal(false);
        getAllCategories();
      } else {
        toast.error(data.message || "Failed to create category");
      }
    } catch {
      toast.error("Error creating category");
    }
    setLoading(false);
  };

  const openEdit = (cat) => {
    setEditCategory(cat);
    setEditName(cat.name);
    setEditImage(null);
    setEditModal(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!editName.trim()) return toast.error("Name is required");
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("name", editName);
      if (editImage) formData.append("image", editImage);
      const { data } = await axios.put(
        `/api/v1/category/update-category/${editCategory._id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (data.success) {
        toast.success("Category updated");
        setEditCategory(null);
        setEditName("");
        setEditImage(null);
        setEditModal(false);
        getAllCategories();
      } else {
        toast.error(data.message || "Update failed");
      }
    } catch {
      toast.error("Error updating");
    }
    setLoading(false);
  };

  const handleDelete = async (id, name) => {
    const result = await Swal.fire({
      title: `Delete "${name}"?`,
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Delete",
      background: "#1f2937",
      color: "#fff",
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const { data } = await axios.delete(
        `/api/v1/category/delete-category/${id}`
      );
      if (data.success) {
        toast.success("Deleted");
        getAllCategories();
      } else {
        toast.error(data.message || "Delete failed");
      }
    } catch {
      toast.error("Error deleting");
    }
    setLoading(false);
  };

  return (
    <Layout title="Category Management">
      <div className="min-h-screen bg-gray-50 mt-16 sm:mt-20">
        <div className="mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
          <div className="mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 truncate">
                  Category Management
                </h1>
                <p className="text-gray-600 mt-1 sm:mt-2 text-xs sm:text-sm md:text-base">
                  Manage all product categories
                </p>
              </div>
              <button
                onClick={() => setCreateModal(true)}
                className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-900 text-white text-sm sm:text-base font-semibold rounded-lg hover:bg-black transition w-full sm:w-auto flex-shrink-0"
              >
                <FiPlus size={18} /> Create Category
              </button>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
            <aside className="w-full lg:w-64 xl:w-72 flex-shrink-0">
              <Sidebar activeTab="categories" auth={auth} />
            </aside>

            <main className="flex-1 min-w-0">
              <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 lg:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Image
                      </th>
                      <th className="px-3 lg:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-3 lg:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Slug
                      </th>
                      <th className="px-3 lg:px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {categories.length === 0 ? (
                      <tr>
                        <td
                          colSpan="4"
                          className="p-8 sm:p-10 text-center text-gray-400 text-sm sm:text-base"
                        >
                          No categories found.
                        </td>
                      </tr>
                    ) : (
                      categories.map((cat) => (
                        <tr
                          key={cat._id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-3 lg:px-4 py-3">
                            {getCategoryImage(cat) ? (
                              <img
                                alt={cat.name}
                                src={getCategoryImage(cat)}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src =
                                    "https://via.placeholder.com/48?text=No+Image";
                                }}
                                className="w-10 h-10 sm:w-12 sm:h-12 object-cover rounded-lg border border-gray-200"
                              />
                            ) : (
                              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gray-200 flex items-center justify-center text-gray-400">
                                <FiImage size={20} />
                              </div>
                            )}
                          </td>
                          <td className="px-3 lg:px-4 py-3 font-semibold text-gray-900 text-sm sm:text-base">
                            {cat.name}
                          </td>
                          <td className="px-3 lg:px-4 py-3 text-gray-600 text-xs sm:text-sm">
                            {cat.slug}
                          </td>
                          <td className="px-3 lg:px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                className="inline-flex items-center gap-1 px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                                onClick={() => openEdit(cat)}
                              >
                                <FiEdit2 size={14} /> Edit
                              </button>
                              <button
                                className="inline-flex items-center gap-1 px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition"
                                onClick={() => handleDelete(cat._id, cat.name)}
                              >
                                <FiTrash2 size={14} /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden space-y-3 sm:space-y-4">
                {categories.length === 0 ? (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-400">
                    No categories found.
                  </div>
                ) : (
                  categories.map((cat) => (
                    <div
                      key={cat._id}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-5"
                    >
                      <div className="flex items-start gap-3 sm:gap-4">
                        {getCategoryImage(cat) ? (
                          <img
                            alt={cat.name}
                            src={getCategoryImage(cat)}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src =
                                "https://via.placeholder.com/48?text=No+Image";
                            }}
                            className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded-lg border border-gray-200 flex-shrink-0"
                          />
                        ) : (
                          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg bg-gray-200 flex items-center justify-center text-gray-400 flex-shrink-0">
                            <FiImage size={24} />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 text-base sm:text-lg mb-1 truncate">
                            {cat.name}
                          </h3>
                          <p className="text-gray-500 text-xs sm:text-sm mb-3 truncate">
                            {cat.slug}
                          </p>

                          <div className="flex gap-2">
                            <button
                              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                              onClick={() => openEdit(cat)}
                            >
                              <FiEdit2 size={14} /> Edit
                            </button>
                            <button
                              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition"
                              onClick={() => handleDelete(cat._id, cat.name)}
                            >
                              <FiTrash2 size={14} /> Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </main>
          </div>
        </div>

        {createModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              backgroundColor: "rgba(0,0,0,0.4)",
            }}
          >
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-200">
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                    Create Category
                  </h2>
                  <button
                    className="text-gray-400 hover:text-gray-600 transition"
                    onClick={() => setCreateModal(false)}
                  >
                    <FiX size={24} />
                  </button>
                </div>
                <form onSubmit={handleCreate}>
                  <div className="mb-4">
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                      Category Name
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter category name"
                    />
                  </div>
                  <div className="mb-6">
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                      Category Image
                    </label>
                    <input
                      type="file"
                      className="w-full border border-gray-300 rounded-lg px-2 sm:px-3 py-2 text-xs sm:text-sm"
                      accept="image/*"
                      onChange={(e) => setImage(e.target.files[0])}
                    />
                  </div>
                  <div className="flex gap-2 sm:gap-3">
                    <button
                      type="button"
                      className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 text-gray-700 text-sm sm:text-base font-semibold rounded-lg hover:bg-gray-50 transition"
                      onClick={() => setCreateModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-900 text-white text-sm sm:text-base font-semibold rounded-lg hover:bg-black transition disabled:opacity-50"
                    >
                      {loading ? "Creating..." : "Create"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {editModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              backgroundColor: "rgba(0,0,0,0.4)",
            }}
          >
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-200">
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                    Edit Category
                  </h2>
                  <button
                    className="text-gray-400 hover:text-gray-600 transition"
                    onClick={() => setEditModal(false)}
                  >
                    <FiX size={24} />
                  </button>
                </div>
                <form onSubmit={handleEdit}>
                  <div className="mb-4">
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                      Category Name
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                    />
                  </div>
                  <div className="mb-6">
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                      Update Image (optional)
                    </label>
                    <input
                      type="file"
                      className="w-full border border-gray-300 rounded-lg px-2 sm:px-3 py-2 text-xs sm:text-sm"
                      accept="image/*"
                      onChange={(e) => setEditImage(e.target.files[0])}
                    />
                  </div>
                  <div className="flex gap-2 sm:gap-3">
                    <button
                      type="button"
                      className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 text-gray-700 text-sm sm:text-base font-semibold rounded-lg hover:bg-gray-50 transition"
                      onClick={() => setEditModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-900 text-white text-sm sm:text-base font-semibold rounded-lg hover:bg-black transition disabled:opacity-50"
                    >
                      {loading ? "Updating..." : "Update"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CategoryManagement;
